import type { SelectedPreviewEntity } from "@components/settings/ChartOfAccountsTemplates/apply/applyCoaTemplatePreviewEntries";
import type { WorkspaceUserAssignment } from "@common/types/workspaceUserAssignment";
import type { CoaTemplateActivityEventRef } from "./coaTemplateActivityEvents";
import { canRoleEditCoa } from "@hooks/permissions/canRoleEditCoa";

interface EntitySummary {
    id: number;
    name: string | null;
    iconColor: string;
    logo: string | null;
}

type WorkspaceRoleAssignment = Pick<WorkspaceUserAssignment, "userId" | "role">;

export interface WorkspaceSummary {
    id: string;
    name: string;
    entities?: EntitySummary[];
    userAssignments?: WorkspaceRoleAssignment[];
}

export function getEditableWorkspaceIds({
    userId,
    workspaces,
}: {
    userId: number | undefined;
    workspaces: WorkspaceSummary[];
}): Set<string> {
    if (!userId) {
        return new Set();
    }
    return new Set(
        workspaces
            .filter((workspace) =>
                workspace.userAssignments?.some(
                    (assignment) =>
                        assignment.userId === userId &&
                        canRoleEditCoa(assignment.role),
                ),
            )
            .map((workspace) => workspace.id),
    );
}

export function buildCoaTemplateActivityEntries({
    eventRefs,
    workspaces,
}: {
    eventRefs: CoaTemplateActivityEventRef[];
    workspaces: WorkspaceSummary[];
}): SelectedPreviewEntity[] {
    const entries: SelectedPreviewEntity[] = [];
    for (const eventRef of eventRefs) {
        const workspace = workspaces.find(
            (candidate) => candidate.id === eventRef.workspaceId,
        );
        const entity = workspace?.entities?.find(
            (candidate) => candidate.id === eventRef.entityId,
        );
        if (!workspace || !entity) {
            continue;
        }
        entries.push({
            entityId: eventRef.entityId,
            name: entity.name ?? "Entity",
            clientLabel: workspace.name,
            workspaceId: eventRef.workspaceId,
            iconColor: entity.iconColor,
            logo: entity.logo,
        });
    }
    return entries;
}
